import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { IItemCreationHeader, ItemCreationSection } from '~/Components';
import { REQUEST_ACTIONS } from '~/Constants/Actions.constants';
import { isObjectEmpty } from '~/Helpers/CleanData.helper';
import {
  useDateFns,
  useNavigation,
  useQuery,
  useLanguage,
  useHasAccess,
  AccessList,
} from '~/Hooks';
import { ILocation } from '~/Models/DTO/Location.models';
import { selectLoading } from '~/store/app';
import { useLocations } from '~/store/provider';
import { ExtendedLocation } from '~/Screens/Settings/ProviderLocations/LocationEdit/ExtendedLocation.model';
import {
  IErrorWeekdays,
  LocationEditDetailSection,
  LocationEditGeneralSection,
  LocationEditOpeningHoursSection,
  LocationEditOtherSection,
  LocationEditSettingsSection,
} from './LocationEditSections';
import { useLocationValidationScheme } from './useLocationValidationScheme';
import { locationDetailVerification, locationVerification } from './verification';
import { QrCode, Hour, Details, GeneralInfo } from '~/Components/Icons';
import { FormikErrors } from 'formik';
import { defaultLatLong } from '~/Constants/Values.constants';
import { useLocationEditSave } from './LocationEditHooks/useLocationEditSave';
import { useMerchant } from '~/ScreensOrganisation/OrganisationMerchant/HelperMerchant/useMerchant';
import { IMerchant } from '~/Models/DTO/Merchant.models';
import { useDefaultCancellationWindow } from '~/requests/eventService/defaultCancellationWindow/useDefaultCancellationWindow';

const _initialValues: ExtendedLocation = {
  uuid: '',
  name: '',
  description: '',
  zipCode: '',
  city: '',
  street: '',
  countryISOA2: '',
  detailsUrl: '',
  howToFind: '',
  imageUrl: '',
  color: '',
  onlineBookingsDisabled: false,
  latitude: defaultLatLong.lat,
  longitude: defaultLatLong.lng,
  latitudeMap: defaultLatLong.lat,
  longitudeMap: defaultLatLong.lng,
  welcomePackageSettings: {
    a5FlyerAmount: 0,
    a6FlyerAmount: 0,
    receiverName: '',
    street: '',
    city: '',
    zipCode: '',
    country: '',
  },
  website: null,
  defaultCancellationWindow: null,
  firstFreeCheckin: false,
};

interface ICheckLocation {
  locationList: ILocation[];
  id: any;
  initialValues: ExtendedLocation | ILocation;
  isCopying: any;
  setInitialValues: (location: ExtendedLocation | ILocation) => void;
  setCurrentLocation: (location: ExtendedLocation | ILocation) => void;
  validate: (location: ExtendedLocation | ILocation) => void;
  merchantList?: IMerchant[];
  currentLanguage?: string | null;
  defaultCancellationWindow: number | null;
}

export const checkLocation = ({
  locationList,
  id,
  initialValues,
  isCopying,
  setInitialValues,
  setCurrentLocation,
  validate,
  merchantList,
  currentLanguage = 'en',
  defaultCancellationWindow,
}: ICheckLocation) => {
  const location = locationList.filter((location) => location.uuid === id)?.[0] || initialValues;
  const merchant = merchantList?.find((merchant) =>
    merchant?.locationIds?.includes(location?.venueAddressId?.toString() || ''),
  )?.uuid;
  const FILE_SUFFIX = 'COPY';

  const cleanedLocation = {
    ...location,
    defaultCancellationWindow,
    description: location.description || '',
    importantInformation: location.importantInformation || '',
    city: location.city || '',
    zipCode: location.zipCode || '',
    countryISOA2: location.countryISOA2 || '',
    latitude: location.latitude || defaultLatLong.lat,
    longitude: location.longitude || defaultLatLong.lng,
    latitudeMap: location.latitude || defaultLatLong.lat,
    longitudeMap: location.longitude || defaultLatLong.lng,
    street: location.street || '',
    name: isCopying ? `${location.name} ${FILE_SUFFIX}` : location.name,
    merchant: merchant || '',
    profileUrl: !!location?.venueAddressId
      ? `https://urbansportsclub.com/${(currentLanguage as string).toLowerCase()}/venues/${
          location?.venueAddressId
        }`
      : '',
  };

  setInitialValues(cleanedLocation);
  setCurrentLocation(cleanedLocation);
  validate(cleanedLocation);
};

export const useLocationEdit = () => {
  // hooks
  const { t } = useTranslation();
  const { getSearchParams } = useNavigation();
  const { id } = getSearchParams();
  const { hasAccess } = useHasAccess();

  const canViewSettingsTab = hasAccess(AccessList.DEFAULT_CANCELATION_WINDOW);

  const {
    locationList,
    errorUpdate,
    isLoadingUpdate,
    isLoadingCreate,
    errorCreate,
  } = useLocations();
  const { data: defaultCancellationWindowResponse } = useDefaultCancellationWindow(
    id,
    canViewSettingsTab,
  );
  const { convertTimeStringToDate, isBeforeDate } = useDateFns();

  const { merchantList } = useMerchant();
  //store
  const imageUploading = useSelector(selectLoading(REQUEST_ACTIONS.UPLOAD_IMAGE));
  const { currentLanguage } = useLanguage();

  // state
  const [initialValues, setInitialValues] = useState<ExtendedLocation | ILocation>(_initialValues);
  const [currentLocation, setCurrentLocation] = useState<ExtendedLocation | ILocation>(
    _initialValues,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const [isGeneralSectionDone, setGeneralSectionDone] = useState(false);
  const [isDetailSectionDone, setDetailSectionDone] = useState(false);
  const [isOpeningHoursSectionDone, setOpeningHoursSectionDone] = useState(false);
  const [isSettingsSectionDone, setSettingsSectionDone] = useState(false);
  const [showFormErrors, setShowFormErrors] = useState(false);
  const query = useQuery();
  const isCopying = query.get('copy');

  const { onLocationSave } = useLocationEditSave({
    currentLocation,
    isCopying: !!isCopying,
    isSubmitDisabled,
    hasValidationErrors,
    setShowFormErrors,
    setIsSubmitDisabled,
    setIsLoading,
  });

  const { validationSchema } = useLocationValidationScheme();

  const shouldBeDisabled =
    isSubmitDisabled || !Boolean(id) || hasValidationErrors || isLoadingUpdate || isLoadingCreate;

  const locationOpeningHoursVerification = (data: ExtendedLocation) => {
    const errors = {} as FormikErrors<ExtendedLocation>;
    const weekdays = {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: [],
    } as IErrorWeekdays;
    const displayWeekdays = data.openingHours?.weekdays;
    Object.keys(weekdays).forEach((day: string) => {
      for (let i = 0, length = displayWeekdays?.[day]?.length || 0; i < length; i++) {
        const block: any = displayWeekdays?.[day][i];
        const blockFromTime = convertTimeStringToDate(block?.from || '');
        const blockToTime = convertTimeStringToDate(
          block?.to === '00:00:00' ? '24:00:00' : block?.to || '',
        );
        if (isBeforeDate(blockToTime.toISOString(), blockFromTime.toISOString())) {
          errors.openingHours = 'validation';
        }
        if (i > 0) {
          const lastBlock: any = displayWeekdays?.[day][i - 1];
          const lastBlockToTime = convertTimeStringToDate(lastBlock?.to || '');
          if (isBeforeDate(blockFromTime.toISOString(), lastBlockToTime?.toISOString())) {
            errors.openingHours = 'validation';
          }
        }
      }
    });
    return errors;
  };

  const locationSettingsVerification = (data: ExtendedLocation) => {
    const errors = {} as FormikErrors<ExtendedLocation>;

    if (data.defaultCancellationWindow !== null) {
      if (data.defaultCancellationWindow === '') {
        errors.defaultCancellationWindow = 'Enter value';
      } else if (data.defaultCancellationWindow < 1 || data.defaultCancellationWindow > 12) {
        errors.defaultCancellationWindow = 'must be between 1 and 12';
      }
    }

    return errors;
  };

  const validate = useCallback((validateLocation: ExtendedLocation) => {
    const errorsLocation = locationVerification(validateLocation);
    const errorsDetail = locationDetailVerification(validateLocation);
    const errorsOpeningHours = locationOpeningHoursVerification(validateLocation);
    const errorsSettings = locationSettingsVerification(validateLocation);
    const shouldDisable =
      !isObjectEmpty(errorsLocation) ||
      !isObjectEmpty(errorsDetail) ||
      !isObjectEmpty(errorsOpeningHours) ||
      !isObjectEmpty(errorsSettings);
    setIsSubmitDisabled(shouldDisable);
    setGeneralSectionDone(isObjectEmpty(errorsLocation));
    setDetailSectionDone(isObjectEmpty(errorsDetail));
    setOpeningHoursSectionDone(isObjectEmpty(errorsOpeningHours));
    setSettingsSectionDone(isObjectEmpty(errorsSettings));
    setCurrentLocation(validateLocation);
  }, []);

  //compute
  const sections = useMemo(() => {
    const list: ItemCreationSection<ExtendedLocation>[] = [
      ({ data }) => LocationEditGeneralSection(data),
      () => LocationEditDetailSection(),
      () => LocationEditOpeningHoursSection(),
      ({ data, setFieldValue }) => LocationEditOtherSection(data, setFieldValue),
    ];

    if (canViewSettingsTab) {
      list.push(() =>
        LocationEditSettingsSection(
          defaultCancellationWindowResponse?.defaultCancellationWindow ?? 0,
        ),
      );
    }

    return list;
  }, [defaultCancellationWindowResponse, canViewSettingsTab]);

  const headers: IItemCreationHeader[] = useMemo(() => {
    const list = [
      {
        index: 0,
        title: t('app.title.general'),
        isDone: isGeneralSectionDone,
        testId: 'locationTab.general',
        icon: <GeneralInfo />,
      },
      {
        index: 1,
        title: t('app.title.details'),
        testId: 'locationTab.details',
        isDone: isDetailSectionDone,
        icon: <Details />,
      },
      {
        index: 2,
        title: t('settings.title.openingHours'),
        testId: 'locationTab.openingHours',
        isDone: isOpeningHoursSectionDone,
        icon: <Hour />,
      },
      {
        index: 3,
        title: t('app.qrCode'),
        testId: 'locationTab.other',
        isDone: true,
        icon: <QrCode />,
      },
    ];

    if (canViewSettingsTab) {
      list.push({
        index: 4,
        title: t('app.settings'),
        testId: 'locationTab.settings',
        isDone: isSettingsSectionDone,
        icon: <></>,
      });
    }

    return list;
  }, [isGeneralSectionDone, isDetailSectionDone, isOpeningHoursSectionDone, isSettingsSectionDone]);

  //lifecycle
  useEffect(() => {
    if (id) {
      checkLocation({
        locationList,
        id,
        initialValues,
        isCopying,
        setInitialValues,
        setCurrentLocation,
        validate,
        merchantList,
        currentLanguage,
        defaultCancellationWindow:
          defaultCancellationWindowResponse?.defaultCancellationWindow ?? null,
      });
    }
  }, [
    id,
    locationList,
    isCopying,
    merchantList,
    defaultCancellationWindowResponse?.defaultCancellationWindow,
  ]);

  return {
    listItems: sections,
    headers,
    onSave: onLocationSave,
    initialValues,
    validate,
    currentLocation,
    error: errorUpdate || errorCreate || {},
    isLoading: isLoadingUpdate || imageUploading || isLoading || isLoadingCreate,
    isSubmitDisabled: shouldBeDisabled,
    validationSchema,
    onValidationErrors: setHasValidationErrors,
    showFormErrors,
  };
};
